/*
* AudioWavSurface.js (Please do not remove this comment)
* This file is part of the Audio Management System.
* This component is a surface for playing audio files using the wavsurfer.js library.
* There are two property (arguments) to be passed to this component:
* 1. blobUrl: A blob URL for the audio file to be played.
* 2. sound level: A sound level for the audio file to be played.
* 
* The component can play, pause, and stop the audio file.
* The component also displays the current time and duration of the audio file.
* The component also displays a waveform of the audio file.
* The component also displays a sound level meter of the audio file.
*
* my version:
* @mui/material: 7.1.0
* @mui/x-date-pickers: 8.3.0
* date-fns: 4.1.0
* wavesurfer.js: 7.9.5
*/

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Slider, Paper, Grid, LinearProgress, Button } from '@mui/material';
import { PlayArrow, Pause, Stop, VolumeUp, Refresh } from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import { format } from 'date-fns';

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '00:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const AudioWavSurface = ({ blobUrl, dbKey, useIndexedDB, soundLevel, onPlayingChange }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const loadAttemptsRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waveformVisible, setWaveformVisible] = useState(false);
  const [localBlobUrl, setLocalBlobUrl] = useState(null); // 用於存儲從 IndexedDB 獲取的 Blob URL
  const [waveSurferInitTries, setWaveSurferInitTries] = useState(0);

  // 從 IndexedDB 讀取音頻
  useEffect(() => {
    // 如果不使用 IndexedDB 或缺少 dbKey，則退出
    if (!useIndexedDB || !dbKey) return;
    
    setIsLoading(true);
    setError(null);
    
    const getAudioFromDB = async () => {
      try {
        // 打開數據庫
        const dbRequest = window.indexedDB.open('AudioCache', 1);
        
        dbRequest.onsuccess = (event) => {
          const db = event.target.result;
          const transaction = db.transaction(['audioFiles'], 'readonly');
          const store = transaction.objectStore('audioFiles');
          
          // 獲取音頻 Blob
          const request = store.get(dbKey);
          
          request.onsuccess = () => {
            if (request.result) {
              // 創建新的 Blob URL
              const newBlobUrl = URL.createObjectURL(request.result);
              
              // 儲存本地 URL 並清理舊的
              setLocalBlobUrl(prevUrl => {
                if (prevUrl) URL.revokeObjectURL(prevUrl);
                return newBlobUrl;
              });
              
              setIsLoading(false);
            } else {
              console.error('Audio not found in IndexedDB:', dbKey);
              setError('Audio file not found in cache');
              setIsLoading(false);
            }
          };
          
          request.onerror = (e) => {
            console.error('Error reading from IndexedDB:', e.target.error);
            setError('Error reading audio from cache');
            setIsLoading(false);
          };
        };
        
        dbRequest.onerror = (e) => {
          console.error('Error opening IndexedDB:', e.target.error);
          setError('Error accessing audio cache');
          setIsLoading(false);
        };
        
      } catch (err) {
        console.error('IndexedDB access error:', err);
        setError('Failed to access audio cache');
        setIsLoading(false);
      }
    };
    
    getAudioFromDB();
    
    // 清理函數 - 釋放創建的 Blob URL
    return () => {
      if (localBlobUrl) {
        URL.revokeObjectURL(localBlobUrl);
      }
    };
  }, [dbKey, useIndexedDB]);
  
  // 確定要使用的實際 Blob URL - 優先使用 IndexedDB 的版本
  const actualBlobUrl = useIndexedDB ? localBlobUrl : blobUrl;
  
  // 重置波形圖狀態
  useEffect(() => {
    // 當 URL 變化時，重置波形狀態
    destroyWaveSurfer();
    setWaveformVisible(false);
    loadAttemptsRef.current = 0;
  }, [actualBlobUrl]);

  useEffect(() => {
    if (waveformVisible && actualBlobUrl) {
      initWaveSurfer();
    }
  }, [waveformVisible, actualBlobUrl]);

  useEffect(() => {
    return () => {
      // 清理 WaveSurfer
      destroyWaveSurfer();
      // 清理 audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);
  
  // 音頻元素初始化 - 修改為使用 actualBlobUrl
  useEffect(() => {
    if (!actualBlobUrl) return;

    setIsLoading(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);

    const audio = new window.Audio(actualBlobUrl);

    // 事件處理函數
    const onLoadedMetadata = () => {
      if (!audioRef.current) return;
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const onTimeUpdate = () => {
      if (!audioRef.current) return;
      setCurrentTime(audio.currentTime);
    };
    const onEnded = () => {
      if (!audioRef.current) return;
      setIsPlaying(false);
      if (onPlayingChange) onPlayingChange(false);
    };
    const onError = (e) => {
      if (!audioRef.current) return;
      console.error('Audio loading error:', e);
      setError('Unable to load audio file');
      setIsLoading(false);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    audio.volume = volume / 100;
    audioRef.current = audio;

    // 清理函數
    return () => {
      audio.pause();
      audio.src = '';
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audioRef.current = null;
    };
  }, [actualBlobUrl, volume, onPlayingChange]);

  // 音量變化處理
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(volume / 100);
    }
  }, [volume]);

  // 修改 WaveSurfer 初始化函數
  const initWaveSurfer = () => {
    console.log("initWaveSurfer called", { actualBlobUrl, container: containerRef.current, tries: waveSurferInitTries });
    console.log("offsetWidth:", containerRef.current ? containerRef.current.offsetWidth : "Container not available");
    if (!containerRef.current || containerRef.current.offsetWidth === 0) {
      if (waveSurferInitTries < 10) {
        setTimeout(() => setWaveSurferInitTries(t => t + 1), 100);
      } else {
        setError("波形容器無法初始化，請檢查畫面寬度設定");
      }
      return;
    }
    setWaveSurferInitTries(0);
    
    // 如果已有實例，先銷毀
    destroyWaveSurfer();
    
    setIsLoading(true);
    setError(null);
    loadAttemptsRef.current += 1;
    
    console.log("初始化 WaveSurfer...", actualBlobUrl);
    
    try {
      // 確保容器清空
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
      
      // 使用最基本配置
      const wavesurfer = WaveSurfer.create({
        container: containerRef.current,
        waveColor: '#4f9df9',
        progressColor: '#1565c0',
        cursorColor: '#1565c0',
        height: 200,
        barWidth: 1,
        barGap: 1,
        // backend: 'WebAudio', // 這裡改成 WebAudio
        backend: 'MediaElementWebAudio',
        normalize: false,
        responsive: true,
        interact: true,
        // 移除可能有問題的高級選項
        partialRender: false,
        fillParent: true,
        scrollParent: false
      });
      console.log("WaveSurfer 實例已創建", wavesurfer);
      
      // 設置超時
      const loadTimeout = setTimeout(() => {
        console.log("WaveSurfer 載入超時");
        if (wavesurferRef.current === wavesurfer) {
          destroyWaveSurfer();
          setError("波形載入超時，請再試一次");
          setIsLoading(false);
        }
      }, 15000);
      
      // 事件監聽器
      wavesurfer.on('ready', () => {
        console.log("WaveSurfer 準備就緒");
        clearTimeout(loadTimeout);
        setIsLoading(false);
        setWaveformVisible(true);
        
        // 同步當前播放狀態
        if (audioRef.current && audioRef.current.currentTime > 0) {
          wavesurfer.seekTo(audioRef.current.currentTime / audioRef.current.duration);
        }
        
        if (isPlaying) {
          wavesurfer.play();
        }
      });
      
      wavesurfer.on('error', err => {
        console.error("WaveSurfer 錯誤:", err);
        clearTimeout(loadTimeout);
        setError(`波形載入失敗: ${err.message || "未知錯誤"}`);
        setIsLoading(false);
        destroyWaveSurfer();
        
        // 嘗試使用另一种方式加載
        if (loadAttemptsRef.current < 2) {
          console.log("嘗試使用另一種方式載入波形...");
          setTimeout(() => {
            loadAttemptsRef.current = 2; // 防止再次自動重試
            tryAlternativeWaveSurfer();
          }, 500);
        }
      });
      
      wavesurfer.on('seek', (pos) => {
        if (audioRef.current) {
          audioRef.current.currentTime = pos * audioRef.current.duration;
        }
      });
      
      // 監聽播放狀態同步
      wavesurfer.on('play', () => {
        if (!isPlaying && audioRef.current) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      });
      
      wavesurfer.on('pause', () => {
        if (isPlaying && audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      });
      
      // 保存引用
      wavesurferRef.current = wavesurfer;
      
      // 加載音頻 - 使用 try-catch 捕獲任何加載錯誤
      try {
        console.log("加載音頻到 WaveSurfer:", actualBlobUrl);
        fetch(actualBlobUrl)
          .then(res => res.arrayBuffer())
          .then(buffer => {
            wavesurfer.loadBlob(new Blob([buffer]));
          });
        console.log("已呼叫 wavesurfer.load");
      } catch (loadErr) {
        console.error("WaveSurfer 加載錯誤:", loadErr);
        clearTimeout(loadTimeout);
        setError("無法載入波形");
        setIsLoading(false);
        destroyWaveSurfer();
      }
      
    } catch (e) {
      console.error("創建 WaveSurfer 實例失敗:", e);
      setError(`無法創建波形視圖: ${e.message}`);
      setIsLoading(false);
    }
  };
  
  // 嘗試替代方案 - 使用 MediaElement 直接解碼
  const tryAlternativeWaveSurfer = async () => {
    if (!actualBlobUrl || !containerRef.current) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 清空容器
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
      
      // 創建一個新的 div 作為真正的容器
      const innerContainer = document.createElement('div');
      containerRef.current.appendChild(innerContainer);
      
      // 先創建音頻元素
      const audioElement = document.createElement('audio');
      audioElement.controls = false;
      audioElement.src = actualBlobUrl;
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
      
      // 創建簡化版 WaveSurfer
      const wavesurfer = WaveSurfer.create({
        container: innerContainer,
        waveColor: '#4f9df9',
        progressColor: '#1565c0',
        height: 60,
        barWidth: 2,
        backend: 'MediaElement',
        mediaControls: false,
        mediaType: 'audio',
        normalize: false
      });
      
      // 直接使用音頻元素
      wavesurfer.on('ready', () => {
        setIsLoading(false);
        setWaveformVisible(true);
        document.body.removeChild(audioElement);
      });
      
      wavesurfer.on('error', err => {
        console.error("Alternative WaveSurfer 錯誤:", err);
        setError("無法載入波形視圖，請使用基本播放器");
        setIsLoading(false);
        if (document.body.contains(audioElement)) {
          document.body.removeChild(audioElement);
        }
      });
      
      wavesurferRef.current = wavesurfer;
      wavesurfer.load(audioElement);
      
    } catch (e) {
      console.error("Alternative WaveSurfer 失敗:", e);
      setError("無法載入波形，請使用基本播放功能");
      setIsLoading(false);
    }
  };
  
  // 清理 WaveSurfer (減少記憶體佔用)
  const destroyWaveSurfer = () => {
    if (wavesurferRef.current) {
      try {
        // 關閉 WebAudio context
        if (
          wavesurferRef.current.backend &&
          wavesurferRef.current.backend.ac &&
          typeof wavesurferRef.current.backend.ac.close === 'function'
        ) {
          wavesurferRef.current.backend.ac.close();
        }
        wavesurferRef.current.destroy();
      } catch (e) {
        console.error("清理 WaveSurfer 失敗:", e);
      }
      wavesurferRef.current = null;
      setWaveformVisible(false);
      console.log("WaveSurfer destroyed");
    }
  };

  // 播放/暫停控制
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error("播放失敗:", err);
        setError("Cannot play audio");
      });
    }
    
    setIsPlaying(!isPlaying);
    
    // 同步 wavesurfer (如果已初始化)
    if (wavesurferRef.current) {
      if (isPlaying) {
        wavesurferRef.current.pause();
      } else {
        wavesurferRef.current.play();
      }
    }
  };

  // 停止按鈕
  const handleStop = () => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    
    // 同步 wavesurfer (如果已初始化)
    if (wavesurferRef.current) {
      wavesurferRef.current.stop();
    }
  };

  // 音量調節
  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  // 計算音量條
  const normalizedSoundLevel = soundLevel ? Math.min(Math.max(0, soundLevel), 120) * (100/120) : 0;

  // 當檔案變動時自動顯示波形
  useEffect(() => {
    if (actualBlobUrl) {
      setWaveformVisible(true);
    }
  }, [actualBlobUrl]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Grid container spacing={2} direction="column">
        {/* 波形顯示 */}
        <Grid item xs={12}>
          <Box 
            ref={containerRef} 
            sx={{ 
              width: '100%', 
              minWidth: 300,
              bgcolor: 'background.default', 
              borderRadius: 1,
              minHeight: '200px',
              display: waveformVisible ? 'block' : 'none',
              mx: 'auto'
            }} 
          />
          {/* 不再顯示載入波形按鈕 */}
        </Grid>

        {/* 控制列移到下方 */}
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexDirection: 'row', // 可改為 'column' 讓按鈕直排
            gap: 2,
            width: '100%',
            mt: 1
          }}>
            <IconButton onClick={togglePlayPause} disabled={isLoading || !blobUrl}>
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <IconButton onClick={handleStop} disabled={isLoading || !blobUrl}>
              <Stop />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
            <VolumeUp sx={{ mr: 1 }} />
            <Slider
              value={volume}
              onChange={handleVolumeChange}
              min={0}
              max={100}
              sx={{ color: 'primary.main', width: 120, mx: 1 }}
              disabled={isLoading || !blobUrl}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Sound Level: {soundLevel ? `${soundLevel.toFixed(1)} dB` : 'N/A'}
          </Typography>
          <Box sx={{ width: '100%', height: 8, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box
              sx={{
                height: '100%',
                width: `${normalizedSoundLevel}%`,
                bgcolor: normalizedSoundLevel < 40 ? 'success.main' :
                        normalizedSoundLevel < 80 ? 'warning.main' : 'error.main',
                borderRadius: 1,
                transition: 'width 0.3s ease-in-out',
              }}
            />
          </Box>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Typography variant="body2" color="error.main" sx={{ mt: 1, textAlign: 'center' }}>
              {error}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default AudioWavSurface;
